import axios, { AxiosResponse } from "axios";
import { isCleApiUrl } from "./cle-api/endpoint";
import { isCleApiError } from "./cle-api/errors";
import { getAuthToken } from "../lib/authToken";

const rejectRequestError = (error: unknown): Promise<never> => {
  if (isCleApiError(error)) {
    return Promise.reject(error);
  }

  if (axios.isAxiosError(error)) {
    return Promise.reject(error.response);
  }

  return Promise.reject(error);
};

export const replaceEmptyString = (body) => {
  if (typeof body === "object" && body !== null) {
    if (Array.isArray(body)) {
      return body.map((item) => replaceEmptyString(item));
    }

    Object.keys(body).forEach((key) => {
      if (body[key] === "") {
        body[key] = null;
      } else if (typeof body[key] === "object") {
        body[key] = replaceEmptyString(body[key]);
      }
    });
  }
  return body;
};

export const QUERY_PAGE_SIZE = 50;

const apimSubscriptionKey = import.meta.env.OCP_APIM_SUBSCRIPTION_KEY as
  string | undefined;

const cleApimSubscriptionKey = import.meta.env.OCP_CLE_APIM_SUBSCRIPTION_KEY as
  string | undefined;

const getApimSubscriptionKeyForUrl = (
  url: string | undefined,
): string | undefined =>
  isCleApiUrl(url) ? cleApimSubscriptionKey : apimSubscriptionKey;

export const axiosConfig = {
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
};

/** Læses én gang og tåler at variablen mangler. Modulet importeres også af
 * unit-tests og i CI, hvor der ikke findes nogen .env — uden fallback kaster
 * .endsWith/.includes nedenfor og river hele modulgrafen med ved import. */
const configuredBackendEndpoint = import.meta.env.VITE_BACKEND_ENDPOINT ?? "";

const withTrailingSlash = (url: string): string =>
  !url || url.endsWith("/") ? url : `${url}/`;

export const backendEndpoint = withTrailingSlash(configuredBackendEndpoint);

export const defaultApiUrl = `${backendEndpoint}api/`;

export const graphqlApiUrl = configuredBackendEndpoint.includes("localhost")
  ? `${backendEndpoint}graphql`
  : `${configuredBackendEndpoint}-graphql`;

// Ensure subscription key and Clerk JWT are sent on every axios request
axios.interceptors.request.use(async (config) => {
  const subscriptionKey = getApimSubscriptionKeyForUrl(config.url);
  if (subscriptionKey) {
    config.headers.set("Ocp-Apim-Subscription-Key", subscriptionKey);
  } else {
    config.headers.delete("Ocp-Apim-Subscription-Key");
  }
  const token = await getAuthToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

export async function get<T>({
  query = "",
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  try {
    const response = await axios.get<T>(`${apiPrefix}${query}`, axiosConfig);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error);
  }
}
export async function post<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  try {
    const response = await axios.post<T>(
      `${apiPrefix}${query}`,
      replaceEmptyString(body),
      axiosConfig,
    );
    return response.data;
  } catch (error: unknown) {
    return rejectRequestError(error);
  }
}
export async function postForm<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  try {
    const response = await axios.postForm<T>(`${apiPrefix}${query}`, body, {
      ...axiosConfig,
    });
    return response.data;
  } catch (error: unknown) {
    return rejectRequestError(error);
  }
}
export async function patch<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  try {
    const response = await axios.patch<T>(`${apiPrefix}${query}`, body, {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        "Content-Type": "application/json-patch+json",
      },
    });
    return response.data;
  } catch (error: unknown) {
    return rejectRequestError(error);
  }
}

export async function put<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  try {
    const response = await axios.put<T>(
      `${apiPrefix}${query}`,
      replaceEmptyString(body),
      axiosConfig,
    );
    return response.data;
  } catch (error: unknown) {
    return rejectRequestError(error);
  }
}

export async function putForm<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  try {
    const response = await axios.putForm<T>(`${apiPrefix}${query}`, body, {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        "Content-Type": "application/json-put+json",
      },
    });
    return response.data;
  } catch (error: unknown) {
    return rejectRequestError(error);
  }
}

export async function del<T>({
  query = "",
  apiPrefix = defaultApiUrl,
}): Promise<AxiosResponse<T>> {
  try {
    return await axios.delete<T>(`${apiPrefix}${query}`, axiosConfig);
  } catch (error: unknown) {
    return rejectRequestError(error);
  }
}

function getFileNameFromContentDisposition(
  contentDisposition?: string,
): string | undefined {
  if (!contentDisposition) return undefined;

  const fileNameStarMatch = contentDisposition.match(
    /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i,
  );
  if (fileNameStarMatch?.[1]) {
    return decodeURIComponent(
      fileNameStarMatch[1].trim().replace(/^"(.*)"$/, "$1"),
    );
  }

  const fileNameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  if (fileNameMatch?.[1]) {
    return fileNameMatch[1].trim().replace(/^"(.*)"$/, "$1");
  }

  return undefined;
}

function getFallbackFileName(url: string): string {
  try {
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
    return pathSegments.at(-1) || "download";
  } catch {
    return "download";
  }
}

/**
 * Fetches a report URL with auth (subscription key + JWT via axios interceptors)
 * and preserves server-provided filenames for downloads when possible.
 */
export async function openReportWithAuth(url: string): Promise<void> {
  const response = await axios.get<Blob>(url, { responseType: "blob" });
  const blob = response.data;
  const blobUrl = URL.createObjectURL(blob);
  const contentDisposition = response.headers["content-disposition"];
  const fileName = getFileNameFromContentDisposition(contentDisposition);

  if (fileName || contentDisposition?.toLowerCase().includes("attachment")) {
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName || getFallbackFileName(url);
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return;
  }

  window.open(blobUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 180_000);
}
